import { getLessonCoachIds, type Coach, type Lesson } from '@court-sync/shared';
import { getLessonDaysOfWeek } from './lessonDays';

type CoachAvailabilitySlot = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
};

type CoachWithAvailability = Coach & {
  availability?: CoachAvailabilitySlot[];
};

export type LessonAvailabilityWarningDays = Map<string, Set<number>>;

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours * 60) + minutes;
}

function isRangeCoveredBySlots(
  slots: CoachAvailabilitySlot[],
  requestedStart: string,
  requestedEnd: string
): boolean {
  const start = timeToMinutes(requestedStart);
  const end = timeToMinutes(requestedEnd);
  if (end <= start) return false;

  const intervals = slots
    .map((slot) => [timeToMinutes(slot.startTime), timeToMinutes(slot.endTime)] as const)
    .filter(([slotStart, slotEnd]) => slotEnd > slotStart)
    .sort((a, b) => a[0] - b[0]);

  if (intervals.length === 0) return false;

  let mergedStart = intervals[0][0];
  let mergedEnd = intervals[0][1];

  for (let i = 1; i < intervals.length; i += 1) {
    const [nextStart, nextEnd] = intervals[i];
    if (nextStart <= mergedEnd) {
      mergedEnd = Math.max(mergedEnd, nextEnd);
      continue;
    }

    if (start >= mergedStart && end <= mergedEnd) {
      return true;
    }

    mergedStart = nextStart;
    mergedEnd = nextEnd;
  }

  return start >= mergedStart && end <= mergedEnd;
}

export function getCoachAvailabilityMissingDays(
  coach: CoachWithAvailability | undefined,
  daysOfWeek: number[],
  startTime: string,
  endTime: string
): number[] {
  const slots = coach?.availability ?? [];
  // No configured weekly availability means "available" by default.
  if (slots.length === 0) return [];

  return daysOfWeek.filter((day) => {
    // Collect only the available slots for this specific day.
    // The availability page saves ONLY isAvailable:true slots; absence of a day's rows
    // means the coach deliberately excluded that day (not "unconfigured").
    const availableSlots = slots.filter((slot) => slot.dayOfWeek === day && slot.isAvailable);
    return !isRangeCoveredBySlots(availableSlots, startTime, endTime);
  });
}

export function computeLessonAvailabilityWarningDays(
  lessons: Lesson[],
  coaches: CoachWithAvailability[]
): LessonAvailabilityWarningDays {
  const warningDays = new Map<string, Set<number>>();
  const coachById = new Map(coaches.map((coach) => [coach.id, coach]));

  for (const lesson of lessons) {
    const coachIds = getLessonCoachIds(lesson);
    const daysOfWeek = getLessonDaysOfWeek(lesson as Lesson & { dayOfWeek?: number });

    if (coachIds.length === 0 || daysOfWeek.length === 0) continue;

    const lessonMissingDays = new Set<number>();
    for (const coachId of coachIds) {
      const coach = coachById.get(coachId);
      const missingDays = getCoachAvailabilityMissingDays(coach, daysOfWeek, lesson.startTime, lesson.endTime);
      missingDays.forEach((day) => lessonMissingDays.add(day));
    }

    if (lessonMissingDays.size > 0) {
      warningDays.set(lesson.id, lessonMissingDays);
    }
  }

  return warningDays;
}

export function computeLessonAvailabilityWarningSet(
  lessons: Lesson[],
  coaches: CoachWithAvailability[]
): Set<string> {
  return new Set(computeLessonAvailabilityWarningDays(lessons, coaches).keys());
}
